import React from 'react';
import { Skeleton, SkeletonLine, SkeletonButton, SkeletonCard } from './Skeleton';

// Form Field Skeleton
interface FormFieldSkeletonProps {
  width?: string;
  hasLabel?: boolean;
  type?: 'input' | 'select' | 'textarea' | 'checkbox' | 'radio';
}

export const FormFieldSkeleton: React.FC<FormFieldSkeletonProps> = ({
  width = '100%',
  hasLabel = true,
  type = 'input'
}) => {
  const getHeight = () => {
    switch (type) {
      case 'textarea': return 'h-24';
      case 'select': return 'h-10';
      case 'checkbox':
      case 'radio': return 'h-5';
      default: return 'h-10';
    }
  };

  return (
    <div className="mb-4" style={{ width }}>
      {hasLabel && <SkeletonLine width="30%" className="mb-2" />}
      {type === 'checkbox' || type === 'radio' ? (
        <div className="flex items-center space-x-2">
          <Skeleton className="w-5 h-5" rounded="sm" />
          <SkeletonLine width="60%" />
        </div>
      ) : (
        <Skeleton className={`w-full ${getHeight()}`} rounded="md" />
      )}
    </div>
  );
};

// Basic Form Skeleton
interface FormSkeletonProps {
  fields?: number;
  columns?: 1 | 2 | 3;
  showButtons?: boolean;
  title?: boolean;
  className?: string;
}

export const FormSkeleton: React.FC<FormSkeletonProps> = ({
  fields = 6,
  columns = 1,
  showButtons = true,
  title = true,
  className = ''
}) => {
  const gridClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
  };

  return (
    <SkeletonCard className={className}>
      {title && (
        <div className="mb-6">
          <SkeletonLine width="40%" className="h-6 mb-2" />
          <SkeletonLine width="60%" />
        </div>
      )}

      <div className={`grid gap-4 ${gridClasses[columns]}`}>
        {Array.from({ length: fields }).map((_, index) => (
          <FormFieldSkeleton
            key={index}
            type={index % 7 === 6 ? 'textarea' : index % 5 === 4 ? 'select' : 'input'}
          />
        ))}
      </div>

      {showButtons && (
        <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
          <SkeletonButton width="80px" />
          <SkeletonButton width="100px" />
        </div>
      )}
    </SkeletonCard>
  );
};

// Patient Form Skeleton
export const PatientFormSkeleton: React.FC = () => (
  <div className="space-y-6">
    <SkeletonCard>
      <SkeletonLine width="200px" className="h-6 mb-6" />
      
      {/* Personal Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <FormFieldSkeleton />
        <FormFieldSkeleton />
        <FormFieldSkeleton />
        <FormFieldSkeleton />
        <FormFieldSkeleton type="select" />
        <FormFieldSkeleton />
      </div>

      {/* Contact Information */}
      <div className="border-t border-gray-200 pt-6">
        <SkeletonLine width="150px" className="h-5 mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormFieldSkeleton />
          <FormFieldSkeleton />
          <FormFieldSkeleton width="100%" />
          <FormFieldSkeleton width="100%" />
        </div>
      </div>
    </SkeletonCard>

    {/* Medical Information */}
    <SkeletonCard>
      <SkeletonLine width="180px" className="h-5 mb-6" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <FormFieldSkeleton type="select" />
        <FormFieldSkeleton />
        <FormFieldSkeleton width="100%" type="textarea" />
      </div>
    </SkeletonCard>

    {/* Emergency Contact */}
    <SkeletonCard>
      <SkeletonLine width="160px" className="h-5 mb-6" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormFieldSkeleton />
        <FormFieldSkeleton />
        <FormFieldSkeleton />
        <FormFieldSkeleton type="select" />
      </div>
    </SkeletonCard>

    {/* Action Buttons */}
    <div className="flex items-center justify-end space-x-3">
      <SkeletonButton width="100px" />
      <SkeletonButton width="120px" />
    </div>
  </div>
);

// Appointment Form Skeleton
export const AppointmentFormSkeleton: React.FC = () => (
  <SkeletonCard>
    <SkeletonLine width="220px" className="h-6 mb-6" />
    
    <div className="space-y-6">
      {/* Patient Selection */}
      <div>
        <SkeletonLine width="120px" className="h-5 mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormFieldSkeleton type="select" hasLabel={false} />
          <FormFieldSkeleton hasLabel={false} />
        </div>
      </div>

      {/* Appointment Details */}
      <div className="border-t border-gray-200 pt-6">
        <SkeletonLine width="160px" className="h-5 mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <FormFieldSkeleton type="select" hasLabel={false} />
          <FormFieldSkeleton type="select" hasLabel={false} />
          <FormFieldSkeleton hasLabel={false} />
          <FormFieldSkeleton hasLabel={false} />
          <FormFieldSkeleton hasLabel={false} />
          <FormFieldSkeleton type="select" hasLabel={false} />
        </div>
      </div>

      {/* Additional Information */}
      <div className="border-t border-gray-200 pt-6">
        <FormFieldSkeleton width="100%" type="textarea" />
      </div>
    </div>

    <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
      <SkeletonButton width="100px" />
      <SkeletonButton width="140px" />
    </div>
  </SkeletonCard>
);

// Settings Form Skeleton
export const SettingsFormSkeleton: React.FC = () => (
  <div className="space-y-6">
    {/* Profile Settings */}
    <SkeletonCard>
      <SkeletonLine width="150px" className="h-5 mb-6" />
      <div className="flex items-start space-x-6 mb-6">
        <div className="flex-shrink-0">
          <Skeleton className="w-24 h-24" rounded="full" />
          <SkeletonButton width="100px" size="sm" className="mt-2" />
        </div>
        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormFieldSkeleton />
          <FormFieldSkeleton />
          <FormFieldSkeleton width="100%" />
          <FormFieldSkeleton />
        </div>
      </div>
    </SkeletonCard>

    {/* Security Settings */}
    <SkeletonCard>
      <SkeletonLine width="140px" className="h-5 mb-6" />
      <div className="space-y-4">
        <FormFieldSkeleton />
        <FormFieldSkeleton />
        <FormFieldSkeleton />
        <div className="flex items-center space-x-3">
          <FormFieldSkeleton type="checkbox" width="auto" hasLabel={false} />
          <SkeletonLine width="200px" />
        </div>
      </div>
    </SkeletonCard>

    {/* Notification Settings */}
    <SkeletonCard>
      <SkeletonLine width="170px" className="h-5 mb-6" />
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="flex items-center justify-between py-2">
            <div>
              <SkeletonLine width="150px" className="mb-1" />
              <SkeletonLine width="200px" />
            </div>
            <Skeleton className="w-12 h-6" rounded="full" />
          </div>
        ))}
      </div>
    </SkeletonCard>

    {/* Save Button */}
    <div className="flex justify-end">
      <SkeletonButton width="120px" />
    </div>
  </div>
);

// Modal Form Skeleton
interface ModalFormSkeletonProps {
  fields?: number;
  title?: string;
}

export const ModalFormSkeleton: React.FC<ModalFormSkeletonProps> = ({
  fields = 4
}) => (
  <div className="bg-white rounded-xl p-6 max-w-md w-full">
    {/* Modal Header */}
    <div className="flex items-center justify-between mb-6">
      <SkeletonLine width="150px" className="h-6" />
      <Skeleton className="w-6 h-6" rounded="sm" />
    </div>

    {/* Form Fields */}
    <div className="space-y-4 mb-6">
      {Array.from({ length: fields }).map((_, index) => (
        <FormFieldSkeleton key={index} />
      ))}
    </div>

    {/* Modal Actions */}
    <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
      <SkeletonButton width="80px" />
      <SkeletonButton width="100px" />
    </div>
  </div>
);

export default FormSkeleton;